<template>
    <div ref="mapElement" class="map hide-overflow" :class="{ loading }" v-if="!isCrawler">
        <div style="position: absolute; background-color: transparent; width: 100%; height: 100%; z-index: 1000; display: none" :class="{ showScrolling: showScrolling }" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { DEFAULT_COORDINATES } from '@/constants';

export default defineComponent({
    props: { isDelivery: Boolean },
    components: {},
    setup(props) {
        const H = (window as any).H;
        const mapElement = ref(null);
        const showScrolling = ref(false);
        const pixelRatio = window.devicePixelRatio || 1;
        const store = useStore();
        const getLimitedNearLocations = computed(() => store.getters.getLimitedNearLocations);
        const isCrawler = computed(() => store.getters.isCrawler);
        const lastCoordinateSearch = computed(() => store.getters.getLastLocationSearched);
        const highlightedId = computed(() => store.getters.getHighlightedLocationId);
        const getDisplayDeliveryStore = computed(() => store.getters.getDisplayDeliveryStore);
        const loading = computed(() => store.getters.processingNearLocations);
        const device = computed(() => store.getters.device);
        const router = useRouter();
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const donatosPin = require(`@/assets/map-pin.svg`);
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const redRobinPin = require(`@/assets/rr-map-pin.svg`);
        const SEARCH_ZOOM = 12;

        let map;
        const platform = new H.service.Platform({
            apikey: process.env.VUE_APP_HERE_API_KEY,
        });

        let defaultLayers = platform.createDefaultLayers({
            tileSize: pixelRatio === 1 ? 256 : 512,
            ppi: pixelRatio === 1 ? undefined : 320,
        });

        const placeMarker = (location) => {
            const lat = location.coordinate.latitude,
                lng = location.coordinate.longitude,
                rr = location.storeStatus?.isRedRobinStore;
            if (!lat) return;

            const singlePin = location.highlighted || props.isDelivery;
            const icon = rr ? redRobinPin : donatosPin;
            const size = singlePin ? { h: 50, w: 50 } : { h: 30, w: 30 };
            const markers = new H.map.Marker({ lat: lat, lng: lng }, { icon: new H.map.Icon(icon, { size: size }), data: location });

            if (!singlePin) {
                markers.addEventListener('tap', (e) => {
                    e.stopPropagation();
                    router.push(`/location/${e.target.data.slug}`);
                });
            }

            return markers;
        };

        const placeResults = () => {
            if (!map) return;
            map.removeObjects(map.getObjects());

            var group: any[] = [];
            const highlighted = getLimitedNearLocations.value.find((l) => l.highlighted);
            let locations;
            if (props.isDelivery) {
                locations = getDisplayDeliveryStore.value ? [getDisplayDeliveryStore.value] : [];
            } else {
                locations = highlighted ? [highlighted] : getLimitedNearLocations.value;
            }

            for (var i = 0, l = locations.length; i < l; ++i) {
                if (placeMarker(locations[i])) {
                    group.push(placeMarker(locations[i]));
                }
            }

            setTimeout(() => {
                if (group.length) {
                    map.removeObjects(map.getObjects());

                    var all = new H.map.Group();
                    all.addObjects(group);
                    map.addObject(all);
                }
            }, 100);
        };

        const renderMap = () => {
            setTimeout(() => {
                map = new H.Map(mapElement.value, defaultLayers.vector.normal.map, {
                    zoom: SEARCH_ZOOM,
                    center: {
                        lat: lastCoordinateSearch.value?.latitude ?? DEFAULT_COORDINATES.latitude,
                        lng: lastCoordinateSearch.value?.longitude ?? DEFAULT_COORDINATES.longitude,
                    },
                    padding: { left: 30, right: 30, top: 30, bottom: 30 },
                });
                const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
                behavior.disable(H.mapevents.Behavior.WHEELZOOM);

                addEventListener('resize', () => map.getViewPort().resize());
                var ui = H.ui.UI.createDefault(map, defaultLayers, 'en-US');
                ui.getControl('mapsettings').setVisibility(false);
                ui.getControl('zoom').setAlignment('right-top');
                ui.getControl('scalebar').setVisibility(false);

                map.addEventListener('tap', (e) => {
                    const clickCoordinates = map.screenToGeo(e.currentPointer.viewportX, e.currentPointer.viewportY);
                    store.dispatch('setNearLocationsByCoordinates', { latitude: clickCoordinates.lat, longitude: clickCoordinates.lng });
                });

                map.addEventListener('pointermove', (event) => (map.getViewPort().element.style.cursor = event.target instanceof H.map.Marker ? 'pointer' : 'auto'), false);

                placeResults();
            }, 500);
        };

        // #region init and watchers
        onMounted(() => renderMap());

        watch(getLimitedNearLocations, () => placeResults());

        watch(highlightedId, () => {
            placeResults();
            const selected = getLimitedNearLocations.value.find((l) => l.highlighted);
            if (!selected || !map) return map?.setZoom(SEARCH_ZOOM);
            map?.setCenter({ lat: selected.coordinate.latitude, lng: selected.coordinate.longitude });
        });

        watch(lastCoordinateSearch, (coords) => {
            map?.setCenter({ lat: coords.latitude, lng: coords.longitude });
            map?.setZoom(SEARCH_ZOOM);
        });

        watch(
            () => props.isDelivery,
            (isDelivery) => {
                placeResults();
                if (isDelivery) {
                    const center = {
                        lat: getDisplayDeliveryStore.value?.coordinate.latitude ?? DEFAULT_COORDINATES.latitude,
                        lng: getDisplayDeliveryStore.value?.coordinate.longitude ?? DEFAULT_COORDINATES.longitude,
                    };
                    map?.setCenter(center);
                } else {
                    const center = {
                        lat: lastCoordinateSearch.value?.latitude ?? DEFAULT_COORDINATES.latitude,
                        lng: lastCoordinateSearch.value?.longitude ?? DEFAULT_COORDINATES.longitude,
                    };
                    map?.setCenter(center);
                }
            }
        );

        watch(getDisplayDeliveryStore, (l) => {
            placeResults();
            map?.setCenter({ lat: l.coordinate.latitude, lng: l.coordinate.longitude });
        });

        watch(device, () => renderMap());
        // #endregion

        // #region override scroll
        var scrolling = false;
        var scrollEnd = undefined;
        function heremapScrollEnd() {
            scrolling = false;
            window.clearTimeout(scrollEnd);
            showScrolling.value = false;
        }
        window.onwheel = function () {
            if (scrolling) {
                window.clearTimeout(scrollEnd);
                window.setTimeout(heremapScrollEnd, 200);
                return;
            }
            scrolling = true;
            showScrolling.value = true;
            window.setTimeout(heremapScrollEnd, 1000);
        };
        //#endregion

        return { mapElement, lastCoordinateSearch, loading, showScrolling, isCrawler };
    },
});
</script>

<style scoped>
.map {
    width: 100%;
    height: 100%;
}

.loading {
    cursor: wait;
}

.showScrolling {
    display: block !important;
}
</style>

<style>
.H_copyright {
    display: none;
}
</style>
